import { Tank } from 'app/agriculture/queries/tanks/types/Tank';
import { useNavigate } from 'react-router-dom';
import { ContentBox } from 'app/agriculture/components/ContentBox/ContentBox';
import { Actions } from 'app/agriculture/components/Actions/Actions';
import { InfoIcon, LocationIcon, TankIcon } from 'app/agriculture/icons';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useContext, useMemo, useState } from 'react';
import { ServicesContext } from 'app/items/shared/context_interface';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import { FormDiscardChangesBlocker } from 'app/agriculture/forms/components/FormDiscardChangesBlocker/FormDiscardChangesBlocker';
import { useRouteActions } from 'app/agriculture/router/useRouteActions';
import { useEntityCreateMutation } from 'app/agriculture/mutations/entity/create/useEntityCreateMutation';
import { Input } from 'app/agriculture/forms/components/Input/Input';
import 'app/agriculture/routes/Tanks/TankCreate/components/TankCreateForm.scss';
import { RenderedMapMarker } from 'app/agriculture/components/Map/RenderedMapMarker/RenderedMapMarker';
import { MapLocation } from 'app/agriculture/queries/types/MapLocation';
import { BannerContent } from 'app/agriculture/Banner/BannerContent';
import { InlineAlert } from 'app/agriculture/components/InlineAlert/InlineAlert';
import { getPlaceholderName } from 'app/agriculture/routes/Infra/utils/getPlaceholderName';
import { useEntities } from 'app/agriculture/queries/entities/useEntities';

type TankCreateFields = Pick<Tank, 'name' | 'geometry'>;

export function TankCreateForm() {
    const navigate = useNavigate();
    const routeActions = useRouteActions();
    const tanks = useEntities('Tank');
    const namePlaceholder = useMemo(
        () => getPlaceholderName({ infraType: 'Tank', entities: tanks }),
        [tanks],
    );
    const formController = useForm<TankCreateFields>({
        mode: 'all',
        defaultValues: {
            name: '',
            geometry: { type: 'Point', coordinates: [0, 0] },
        },
        shouldFocusError: true,
    });
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        getValues,
        clearErrors,
    } = formController;

    const { mutate, isPending } = useEntityCreateMutation('Tank');
    const { agricultureToastService } = useContext(ServicesContext);

    function onSuccess({ id }: { id: string }) {
        agricultureToastService
            .showToast({
                title: 'Tank has been saved',
                subtext: 'The tank was saved successfully!',
                type: AgricultureToastType.SUCCESS,
                variant: 'detailed',
                actions: [
                    {
                        text: 'Add Another Tank',
                        onClick: () => {
                            routeActions.create('Tank');
                        },
                        startIcon: 'add',
                    },
                    {
                        text: 'Edit Tank',
                        onClick: () => {
                            routeActions.edit(id, { type: 'Tank' });
                        },
                        startIcon: 'edit',
                        variant: 'textonly',
                    },
                ],
            })
            .catch((e: unknown) => {
                console.error(e);
            });
        reset();
        routeActions.goto(id, { type: 'Tank' });
    }

    const onSubmit: SubmitHandler<TankCreateFields> = (formData) => {
        mutate(
            {
                ...formData,
                name: formData.name === '' ? namePlaceholder : formData.name,
            },
            { onSuccess },
        );
    };

    const [coordinates, setCoordinates] = useState<MapLocation>({
        latitude: getValues('geometry.coordinates.1'),
        longitude: getValues('geometry.coordinates.0'),
    });

    register('geometry.coordinates', {
        validate: ([lat, lon]) => (lat !== 0 && lon !== 0) || 'Please select a location on the map',
    });

    return (
        <ContentBox title="Create Tank" Icon={TankIcon} className="TankCreateForm" variant="md">
            <FormProvider {...formController}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormDiscardChangesBlocker />
                    {errors.geometry?.coordinates?.message == null ? null : (
                        <ContentBox.Section>
                            <InlineAlert type={AgricultureToastType.ERROR}>
                                {errors.geometry.coordinates.message}
                            </InlineAlert>
                        </ContentBox.Section>
                    )}
                    <ContentBox.Section scrollable className="TankCreateForm__fields">
                        <Input
                            label="Name"
                            {...register('name')}
                            placeholder={namePlaceholder}
                            errorMessage={errors.name?.message}
                        />
                        <RenderedMapMarker
                            value={coordinates}
                            onChange={({ latitude, longitude }) => {
                                setValue('geometry.coordinates.1', latitude, {
                                    shouldDirty: true,
                                });
                                setValue('geometry.coordinates.0', longitude, {
                                    shouldDirty: true,
                                });
                                clearErrors('geometry.coordinates');
                                setCoordinates({ latitude, longitude });
                            }}
                        />

                        <BannerContent>
                            <>
                                <section className="banner-section">
                                    <LocationIcon color="#3098E8" size={18} />
                                    <h4>Set location tool</h4>
                                </section>
                                <section className="banner-section">
                                    <InfoIcon color="#3098E8" size={18} />
                                    <p>Left click to place a Tank</p>
                                </section>
                            </>
                        </BannerContent>

                        <Actions
                            sticky
                            primaryAction={{
                                text: 'Create tank',
                                type: 'submit',
                                isLoading: isPending,
                            }}
                            backAction={{
                                text: 'Back',
                                onClick: () => {
                                    navigate(-1);
                                },
                            }}
                        />
                    </ContentBox.Section>
                </form>
            </FormProvider>
        </ContentBox>
    );
}
